import { useCallback, useEffect, useMemo } from 'react';
import { getUrnByViewerUrn } from '../models/modelManagement';
export let Color;

(function (Color) {
  Color["GREEN"] = "GREEN";
  Color["RED"] = "RED";
  Color["YELLOW"] = "YELLOW";
  Color["GRAY"] = "GRAY";
  Color["WHITE"] = "WHITE";
})(Color || (Color = {}));

export function useThemeManagement({
  geometryLoaded,
  viewer,
  themeData: rawThemeData,
  defaultColor,
  loadModelByUrl
}) {
  const themeData = useMemo(() => loadModelByUrl ? Object.entries(rawThemeData || {}).reduce((acc, [urn, value]) => {
    acc[urn] = value;
    return acc;
  }, {}) : rawThemeData, [loadModelByUrl, rawThemeData]);
  const createTheme = useCallback(params => {
    const {
      geometryLoaded,
      themeData,
      viewer,
      defaultColor
    } = params;
    const models = viewer.getVisibleModels();
    models.forEach(model => model.clearThemingColors());
    viewer.resize();
    const themeModels = models.filter(model => {
      const realUrn = getUrnByViewerUrn(model.getData().urn);

      if (!realUrn) {
        return false;
      }

      return geometryLoaded[realUrn] && themeData[realUrn];
    });

    if (defaultColor && themeModels.length) {
      models.forEach(model => viewer.setThemingColor(1, getColor(defaultColor), model, true));
    }

    themeModels.forEach(model => {
      const realUrn = getUrnByViewerUrn(model.getData().urn);

      if (!realUrn) {
        return;
      }

      const colorData = themeData[realUrn];

      if (!Object.keys(colorData).length) {
        model.clearThemingColors();
        return;
      }

      Object.entries(colorData).forEach(([key, externalIds]) => {
        if (!(externalIds !== null && externalIds !== void 0 && externalIds.length)) {
          return;
        }

        const color = getColor(key);

        for (const externalId of externalIds) {
          const objectId = geometryLoaded[realUrn][externalId];
          viewer.setThemingColor(objectId, color, model, true);
        }
      });
    });
  }, []);
  useEffect(() => {
    if (viewer && themeData && geometryLoaded) {
      createTheme({
        themeData,
        viewer,
        geometryLoaded,
        defaultColor
      });
    }
  }, [createTheme, geometryLoaded, themeData, viewer, defaultColor]);
}

function getColor(color) {
  switch (color) {
    case Color.GREEN:
      return new THREE.Vector4(0, 1, 0, 1);

    case Color.RED:
      return new THREE.Vector4(1, 0, 0, 1);

    case Color.YELLOW:
      return new THREE.Vector4(1, 1, 0, 1);

    case Color.WHITE:
      return new THREE.Vector4(1, 1, 1, 1);

    case Color.GRAY:
      return new THREE.Vector4(0.7, 0.7, 0.7, 1);
  }
}

export function isModelThemeActive(params) {
  const fragList = params.model.getFragmentList();
  const colorMap = fragList.db2ThemingColor;
  return !!colorMap.length;
}